/// <amd-module name="Build/Admin/Pool/Components/PoolDetailsContainer" />
import * as React from "react";

import { IPeoplePickerProvider, PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";

import { Card } from "VSSUI/Card";
import { TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IdentityPickerDropdown, IIdentity } from "VSSUI/IdentityPicker";

import { TaskAgentPool } from "DistributedTask/Client/Contracts/TaskAgent";

import { Owner } from "Build/Admin/Common/Components/Owner";
import { StoreStatus } from "Build/Admin/Common/Constants";
import { PoolActionCreator } from "Build/Admin/Pool/Actions/PoolActionCreator";
import { Description } from "Build/Admin/Pool/Components/Description";
import { StoresHub } from "Build/Admin/Pool/Stores/StoresHub";

import * as Resources from "Build/Admin/Pool/Resources";

export interface IPoolDetailsContainerProps {
    actionCreator: PoolActionCreator;
    storesHub: StoresHub;
}

export interface IPoolDetailsContainerState {
    agentPool: TaskAgentPool | undefined;
    hasManagePoolPermission: boolean;
    agentPoolDescription: string;
    editingDescription: boolean;
    status: StoreStatus;
    owner: IIdentity | undefined;
    undoCount: number; // Used to re-render the identity picker on undo because otherwise the identity picker's textbox doesn't re-render
}

export class PoolDetailsContainer extends VssComponent<IPoolDetailsContainerProps, IPoolDetailsContainerState> {
    private _peoplePickerProvider: IPeoplePickerProvider;

    constructor(props: IPoolDetailsContainerProps, context: IVssComponentContext) {
        super(props, context);
        this.state = this._getStateFromStores();

        this._peoplePickerProvider = new PeoplePickerProvider(context.pageContext, {
            identityTypes: ["user", "group"],
            options: {
                MinResults: 5,
                MaxResults: 20
            },
            operationScopes: ["ims"]
        });
    }

    public render(): JSX.Element {
        return (
            <div className="pipelines-pool-details">
                {
                    this.state.agentPool &&
                    this.state.agentPool.owner &&
                    <div className="page-content-top page-content-left page-content-right">
                        <Card className="owner-card flex-grow"
                            titleProps={{ text: Resources.PoolOwnerLabel, size: TitleSize.Medium }}
                            headerCommandBarItems={this._getOwnerCommandBarItems()}>
                            {
                                this.state.hasManagePoolPermission &&
                                <IdentityPickerDropdown
                                    key={this.state.undoCount}
                                    pickerProvider={this._peoplePickerProvider}
                                    value={this.state.owner}
                                    onChange={this._onOwnerChange} />
                            }
                            {
                                !this.state.hasManagePoolPermission &&
                                <Owner identity={this.state.agentPool.owner} />
                            }
                        </Card>
                    </div>
                }
                <div className="page-content page-content-top">
                    <Card className="description-card flex-grow"
                        titleProps={{ text: Resources.PoolDescriptionLabel, size: TitleSize.Medium }}
                        headerCommandBarItems={this._getDescriptionCommandBarItems()}>
                        <Description
                            agentPoolDescription={this.state.agentPoolDescription}
                            status={this.state.status}
                            editable={this.state.hasManagePoolPermission && this.state.editingDescription}
                            onChanged={this._onDescriptionChanged} />
                    </Card>
                </div>
            </div>
        );
    }

    public componentDidMount(): void {
        this.props.storesHub.descriptionStore.addChangedListener(this._onChanged);
        this.props.storesHub.selectionStore.addChangedListener(this._onChanged);
        this.props.storesHub.securityStore.addChangedListener(this._onChanged);
    }

    public componentWillUnmount(): void {
        this.props.storesHub.descriptionStore.removeChangedListener(this._onChanged);
        this.props.storesHub.selectionStore.removeChangedListener(this._onChanged);
        this.props.storesHub.securityStore.removeChangedListener(this._onChanged);
    }

    private _getOwnerCommandBarItems = (): IHeaderCommandBarItem[] => {
        return [
            {
                id: "undo",
                text: Resources.Undo,
                disabled: !this._ownerIsDirty(),
                onActivate: () => {
                    this.setState({
                        owner: this._getOwnerIdentity(this.props.storesHub.selectionStore.state.selectedAgentPool),
                        undoCount: this.state.undoCount + 1
                    });
                }
            },
            {
                important: true,
                id: "save",
                text: Resources.Save,
                disabled: !this._ownerIsDirty() || !this._ownerIsValid(),
                onActivate: () => {
                    if (this._ownerIsDirty() && this._ownerIsValid() && this.state.owner) {
                        this.props.actionCreator.setPoolOwner(this.state.owner);
                    }
                },
                isPrimary: true
            }
        ];
    }

    private _ownerIsDirty(): boolean {
        const identity: IIdentity | undefined = this._getOwnerIdentity(this.state.agentPool);

        const oldOwnerDescriptor: string | undefined = identity ? identity.subjectDescriptor : undefined;
        const newOwnerDescriptor: string | undefined = this.state.owner ? this.state.owner.subjectDescriptor : undefined;

        return oldOwnerDescriptor !== newOwnerDescriptor;
    }

    private _ownerIsValid(): boolean {
        return this.state.owner !== undefined;
    }

    private _getOwnerIdentity(agentPool: TaskAgentPool | undefined): IIdentity | undefined {
        let identityRef: IdentityRef | undefined;

        if (agentPool) {
            identityRef = agentPool.owner ? agentPool.owner : agentPool.createdBy;
        }

        if (identityRef) {
            return {
                displayName: identityRef.displayName,
                image: getIdentityImageUrl(this.context.pageContext, identityRef),
                subjectDescriptor: identityRef.descriptor
            } as IIdentity;
        }
        else {
            return undefined;
        }
    }

    private _onOwnerChange = (item?: IIdentity): boolean | void => {
        this.setState({
            owner: item
        });
    }

    private _getDescriptionCommandBarItems = (): IHeaderCommandBarItem[] => {
        return [
            {
                id: "edit-view",
                text: this.state.editingDescription ? Resources.View : Resources.Edit,
                disabled: !this.state.hasManagePoolPermission,
                onActivate: () => {
                    this.setState({
                        editingDescription: !this.state.editingDescription
                    });
                }
            },
            {
                id: "undo",
                text: Resources.Undo,
                disabled: !this._descriptionIsDirty(),
                onActivate: () => {
                    this.setState({
                        agentPoolDescription: this.props.storesHub.descriptionStore.state.description
                    });
                }
            },
            {
                important: true,
                id: "save",
                text: Resources.Save,
                disabled: !this._descriptionIsDirty(),
                onActivate: () => {
                    if (this._descriptionIsDirty() && this.state.agentPool) {
                        this.props.actionCreator.setAgentPoolDescription(this.state.agentPool, this.state.agentPoolDescription || "")
                    }
                },
                isPrimary: true
            }
        ];
    }

    private _descriptionIsDirty(): boolean {
        return this.props.storesHub.descriptionStore.state.description !== this.state.agentPoolDescription;
    }

    private _onDescriptionChanged = (agentPoolDescription: string): void => {
        this.setState({
            agentPoolDescription
        });
    }

    private _onChanged = (): void => {
        this.setState(this._getStateFromStores());
    }

    private _getStateFromStores(): IPoolDetailsContainerState {
        return {
            agentPool: this.props.storesHub.selectionStore.state.selectedAgentPool,
            hasManagePoolPermission: this.props.storesHub.securityStore.state.hasManagePoolPermission,
            agentPoolDescription: this.props.storesHub.descriptionStore.state.description,
            editingDescription: false,
            status: this.props.storesHub.descriptionStore.state.status,
            owner: this._getOwnerIdentity(this.props.storesHub.selectionStore.state.selectedAgentPool),
            undoCount: 0
        };
    }
}
